import { Component, Input, OnInit, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { FileRestrictions, UploadEvent, UploadsModule } from '@progress/kendo-angular-upload';
import { BehaviorSubject, combineLatest, Subject, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AttachmentService, IAttachmentResponse } from '../services/attachment.service';
import { ThumbnailCacheService } from '../services/thumbnail-cache.service';
import { resizeImageTo1920 } from '../helpers/image-scaling';
import { downloadBlob, downloadBlobDirect } from '../helpers/fileHelper';
import { TokenStorageService } from 'src/app/authentication/storages/token-storage.service';
import { WindowService } from '@progress/kendo-angular-dialog';
import { KENDO_PDFVIEWER } from '@progress/kendo-angular-pdfviewer';
import { DialogModule, WindowModule } from '@progress/kendo-angular-dialog';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { SharedModule } from "../shared.module";
import { CommonModule } from '@angular/common';




@Component({
    selector: 'app-attachments',
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
    imports: [
        KENDO_PDFVIEWER,
        UploadsModule,
        DialogModule,
        WindowModule,
        ButtonModule,
        SharedModule,
        CommonModule
    ]
})
export class AttachmentsComponent implements OnInit, OnDestroy {
  @Input() assignmentId: string | null = null;
  @Input() contactId: string | null = null;
  @Input() employeeId: string | null = null;
  @Input() expenseId: string | null = null;
  @Input() uploadTitle: string | null = null;
  /** When true, hides upload and delete actions — attachments are view/download only. */
  @Input() readonly = false;

  private destroy$ = new Subject<void>();

  documents$ = new BehaviorSubject<IAttachmentResponse[]>([]);
  isUploading$ = new BehaviorSubject<boolean>(false);

  public previewOpen = false;
  public previewBlobUrl: string | null = null;
  public previewBlob: Blob | null = null;
  public previewFileName: string = '';
  public previewId: string = '';
  public previewIsPdf = false;
  public pdfViewerTools = ['pager', 'spacer', 'zoomInOut', 'zoom', 'selection', 'spacer', 'search', 'print', 'download'];

  vm$: Observable<{
    documents: IAttachmentResponse[];
    isUploading: boolean;
  }>;

  restrictions: FileRestrictions = {
    allowedExtensions: ['.jpg', '.jpeg', '.png', 'webp', '.pdf'],
  };

  public documentDeletionDialogOpened = false;
  public selectedItemToDelete: string | null = null;

  constructor(
    private attachmentService: AttachmentService,
    private tokenStorageService: TokenStorageService,
    private windowService: WindowService,
    public thumbnailCache: ThumbnailCacheService
  ) {
    this.vm$ = combineLatest([
      this.documents$,
      this.isUploading$,
    ]).pipe(
      map(([documents, isUploading]) => ({ documents, isUploading }))
    );
  }

  ngOnInit() {
    this.loadAttachments();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupPreview();
  }

  private cleanupPreview() {
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
    this.previewBlob = null;
  }

  private loadAttachments() {
    let request$: Observable<any>;

    if (this.assignmentId) {
      request$ = this.attachmentService.getAttachmentsByAssignment({ assignmentId: this.assignmentId });
    } else if (this.contactId) {
      request$ = this.attachmentService.getAttachmentsByContact({ contactId: this.contactId });
    } else if (this.employeeId) {
      request$ = this.attachmentService.getAttachmentsByEmployee({ employeeId: this.employeeId });
    } else if (this.expenseId) {
      request$ = this.attachmentService.getAttachmentsByExpense({ expenseId: this.expenseId });
    } else {
      return;
    }

    request$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      const attachments: IAttachmentResponse[] = response.data ?? response;
      this.documents$.next(attachments);
      this.loadThumbnails(attachments);
    });
  }

  private loadThumbnails(attachments: IAttachmentResponse[]) {
    const currentIds = new Set(attachments.map(a => a.id));
    this.thumbnailCache.syncToAttachments(currentIds);

    for (const att of attachments) {
      if (this.thumbnailCache.has(att.id)) continue;

      this.thumbnailCache.getThumbnailUrl(att.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  public openPreview(id: string, fileName: string) {
    this.attachmentService
      .download({ attachmentId: id })
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        const blob: Blob = response instanceof Blob ? response : response.body;
  
        this.cleanupPreview();

        this.previewBlob = blob;
        this.previewBlobUrl = URL.createObjectURL(blob);
        this.previewFileName = fileName;
        this.previewId = id;
        this.previewIsPdf = fileName.toLowerCase().endsWith('.pdf');
        this.previewOpen = true;
      });
  }

  public closePreview() {
    this.previewOpen = false;
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
    this.previewBlob = null;
  }


  public async uploadAttachment(event: UploadEvent): Promise<void> {
    event.preventDefault();
    this.isUploading$.next(true);

    for (const file of event.files) {
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });

      const formData = new FormData();
      formData.append('file', processedFile, file.name);

      if (this.assignmentId) formData.append('assignmentId', this.assignmentId);
      if (this.contactId) formData.append('contactId', this.contactId);
      if (this.employeeId) formData.append('employeeId', this.employeeId);
      if (this.expenseId) formData.append('expenseId', this.expenseId);

      formData.append('tenantId', this.tokenStorageService.getTenantId());

      try {
        const result = await this.attachmentService.upload(formData).toPromise();
        if (result?.id) {
          await this.attachmentService.waitForThumbnail(result.id, 5, 1500);
        }
      } catch (err) {
        console.error('Upload failed', err);
      }
    }

    this.isUploading$.next(false);
    this.loadAttachments();
  }

  public downloadAttachment(id: string, filename?: string) {
    if (this.previewOpen && this.previewId === id && this.previewBlob) {
      downloadBlobDirect(this.previewBlob, filename ?? this.previewFileName ?? 'unbenannt');
      return;
    }
    this.attachmentService
      .download({ attachmentId: id })
      .subscribe(response => downloadBlob(response, filename ?? 'unbenannt'));
  }

  public deleteAttachment(id: string) {
    this.attachmentService.delete(id).subscribe(() => this.loadAttachments());
  }

  public documentDeletionDialogOpen(id: string) {
    this.documentDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }

  public documentDeletionDialogClose(status: string) {
    this.documentDeletionDialogOpened = false;
    if (status === 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete);
    }
    this.selectedItemToDelete = null;
  }
}